import { useState, useEffect, useRef } from 'react';
import { useQuery } from '@tanstack/react-query';
import { parseLocalDate } from '../utils/dates';
import './UpcomingEvents.css';
import EventCard from './EventCard';

const arrowRight = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%236d28d9' stroke-width='2.5' stroke-linecap='round'%3E%3Cline x1='5' y1='12' x2='19' y2='12'/%3E%3Cpolyline points='12 5 19 12 12 19'/%3E%3C/svg%3E";
const searchIcon = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%239ca3af' stroke-width='2' stroke-linecap='round'%3E%3Ccircle cx='11' cy='11' r='8'/%3E%3Cline x1='21' y1='21' x2='16.65' y2='16.65'/%3E%3C/svg%3E";

const API = '/api';

function formatFecha(dateStr) {
  if (!dateStr) return '';
  const d = parseLocalDate(dateStr);
  return d.toLocaleDateString('es-CO', { month: 'short', day: 'numeric' });
}

function formatHora(dateStr) {
  if (!dateStr) return '';
  const d = parseLocalDate(dateStr);
  return d.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit', hour12: true });
}

function formatPrecio(price) {
  if (!price && price !== 0) return '';
  return '$' + Number(price).toLocaleString('es-CO');
}

export default function UpcomingEvents() {
  // Paginación
  const [paginaActual, setPaginaActual] = useState(1);
  const seccionRef = useRef(null);
  const shouldScrollRef = useRef(false);

  // Filtros
  const [filtroBusqueda, setFiltroBusqueda] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroCiudad, setFiltroCiudad] = useState('');
  const [filtroEscenario, setFiltroEscenario] = useState('');
  const [filtroFechaDesde, setFiltroFechaDesde] = useState('');
  const [filtroFechaHasta, setFiltroFechaHasta] = useState('');

  // Cargar filtros una sola vez (React Query)
  const { data: filtersData } = useQuery({
    queryKey: ['upcomingFilters'],
    queryFn: async () => {
      const [catRes, escRes] = await Promise.all([
        fetch(`${API}/categorias`).then(r => r.json()),
        fetch(`${API}/escenarios`).then(r => r.json()),
      ]);
      const cats = catRes.exito ? catRes.datos : [];
      const esc = escRes.exito ? escRes.datos : [];
      const cities = [...new Set(esc.map(e => e.city).filter(Boolean))].sort();
      return { cats, esc, cities };
    },
    staleTime: 600000,
  });

  const categorias = filtersData?.cats || [];
  const escenarios = filtersData?.esc || [];
  const ciudades = filtersData?.cities || [];

  // Params para eventos
  const params = new URLSearchParams({ per_page: '9', page: paginaActual.toString() });
  if (filtroBusqueda) params.set('search', filtroBusqueda);
  if (filtroCategoria) params.set('category_id', filtroCategoria);
  if (filtroCiudad) params.set('city', filtroCiudad);
  if (filtroEscenario) params.set('venue_id', filtroEscenario);
  if (filtroFechaDesde) params.set('date_from', filtroFechaDesde);
  if (filtroFechaHasta) params.set('date_to', filtroFechaHasta);

  // Cargar eventos (React Query)
  const { data: eventsData, isFetching: loading } = useQuery({
    queryKey: ['upcomingEvents', params.toString()],
    queryFn: async () => {
      const res = await fetch(`${API}/eventos?${params}`).then(r => r.json());
      if (!res.exito) throw new Error('Failed to fetch events');
      return res.datos;
    },
    keepPreviousData: true,
  });

  const eventos = eventsData?.data || [];
  const totalPaginas = eventsData?.last_page || 1;

  // Manejar el scroll persistente post-fetch
  useEffect(() => {
    if (!loading && shouldScrollRef.current && seccionRef.current) {
      shouldScrollRef.current = false;
      requestAnimationFrame(() => {
        seccionRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  }, [loading]);

  const limpiarFiltros = () => {
    setFiltroBusqueda('');
    setFiltroCategoria('');
    setFiltroCiudad('');
    setFiltroEscenario('');
    setFiltroFechaDesde('');
    setFiltroFechaHasta('');
    setPaginaActual(1);
  };

  // paginaActual is reset in each filter's onChange handler below

  const hayFiltrosActivos = filtroBusqueda || filtroCategoria || filtroCiudad || filtroEscenario || filtroFechaDesde || filtroFechaHasta;

  return (
    <section className="proximos" ref={seccionRef}>
      <div className="proximos__header">
        <div className="proximos__header-text">
          <h2 className="titulo-seccion">Próximos Eventos</h2>
          <p className="subtitulo-seccion">
            Descubre los mejores eventos en toda Colombia, filtra por ciudad, escenario o categoría.
          </p>
        </div>
        <a href="/eventos" className="proximos__link">
          Filtros
          <img src={arrowRight} alt="" />
        </a>
      </div>

      {/* Filtros */}
      <div className="proximos__filtros">
        <div className="proximos__busqueda">
          <img className="proximos__busqueda-icon" src={searchIcon} alt="" />
          <input
            type="text"
            className="proximos__busqueda-input"
            value={filtroBusqueda}
            onChange={e => { setFiltroBusqueda(e.target.value); setPaginaActual(1); }}
            placeholder="Buscar eventos..."
          />
        </div>

        <label className="sr-only" htmlFor="filtro-ciudad">Ciudad</label>
        <select
          id="filtro-ciudad"
          className="proximos__filtro"
          value={filtroCiudad}
          onChange={e => { setFiltroCiudad(e.target.value); setPaginaActual(1); }}
        >
          <option value="">Ciudad</option>
          {ciudades.map(c => (
            <option key={c} value={c}>{c}</option>
          ))}
        </select>

        <label className="sr-only" htmlFor="filtro-escenario">Escenario</label>
        <select
          id="filtro-escenario"
          className="proximos__filtro"
          value={filtroEscenario}
          onChange={e => { setFiltroEscenario(e.target.value); setPaginaActual(1); }}
        >
          <option value="">Escenario</option>
          {escenarios.map(e => (
            <option key={e.id} value={e.id}>{e.name}</option>
          ))}
        </select>

        <label className="sr-only" htmlFor="filtro-categoria">Categoria</label>
        <select
          id="filtro-categoria"
          className="proximos__filtro"
          value={filtroCategoria}
          onChange={e => { setFiltroCategoria(e.target.value); setPaginaActual(1); }}
        >
          <option value="">Categoría</option>
          {categorias.map(c => (
            <option key={c.id} value={c.id}>{c.name}</option>
          ))}
        </select>

        <input
          type="date"
          className="proximos__filtro proximos__filtro--fecha"
          value={filtroFechaDesde}
          onChange={e => { setFiltroFechaDesde(e.target.value); setPaginaActual(1); }}
          placeholder="Desde"
        />

        <input
          type="date"
          className="proximos__filtro proximos__filtro--fecha"
          value={filtroFechaHasta}
          onChange={e => { setFiltroFechaHasta(e.target.value); setPaginaActual(1); }}
          placeholder="Hasta"
        />

        {hayFiltrosActivos && (
          <button className="proximos__filtro-limpiar" onClick={limpiarFiltros}>
            Limpiar
          </button>
        )}
      </div>

      {/* Grid de eventos */}
      {loading ? (
        <div className="proximos__loading">
          <div className="proximos__spinner" />
          <p>Cargando eventos...</p>
        </div>
      ) : eventos.length === 0 ? (
        <div className="proximos__vacio">
          <p>No se encontraron eventos{hayFiltrosActivos ? ' con los filtros seleccionados' : ''}.</p>
          {hayFiltrosActivos && (
            <button className="boton-contorno" onClick={limpiarFiltros}>Limpiar filtros</button>
          )}
        </div>
      ) : (
        <>
          <div className="proximos__grid">
            {eventos.map(ev => {
              const minPrice = ev.ticket_types?.length
                ? Math.min(...ev.ticket_types.map(t => Number(t.price)))
                : null;
              return (
                <EventCard
                  key={ev.id}
                  id={ev.id}
                  slug={ev.slug}
                  image={ev.image_thumb_url || ev.image_url || 'https://images.unsplash.com/photo-1501281668745-f7f57925c3b4?w=400&q=80'}
                  category={ev.category?.name || ''}
                  date={formatFecha(ev.starts_at)}
                  time={formatHora(ev.starts_at)}
                  venue={ev.venue?.name || ''}
                  city={ev.venue?.city || ''}
                  title={ev.title}
                  price={minPrice != null ? formatPrecio(minPrice) : ''}
                />
              );
            })}
          </div>

          {/* Paginación */}
          {totalPaginas > 1 && (
            <div className="proximos__paginacion">
              <button 
                disabled={paginaActual === 1}
                onClick={() => { shouldScrollRef.current = true; setPaginaActual(p => Math.max(1, p - 1)); }}
                className="boton-contorno"
              >
                Anterior
              </button>
              <span className="proximos__paginas">
                Página {paginaActual} de {totalPaginas}
              </span>
              <button 
                disabled={paginaActual === totalPaginas}
                onClick={() => { shouldScrollRef.current = true; setPaginaActual(p => Math.min(totalPaginas, p + 1)); }}
                className="boton-contorno"
              >
                Siguiente
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
